import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

// Base URL and API version for message-related endpoints
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const MESSAGE_API_VERSION = 'v1'
const ACCOUNT_API_VERSION = 'v1'

// Async thunk to fetch all messages exchanged with a given user
export const getMessages = createAsyncThunk(
	'messages/getMessages',
	async (otherMemberId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/get-messages/${otherMemberId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getDirectChatUsers = createAsyncThunk(
	'messages/getDirectChatUsers',
	async ({ chatType = 'outSide', kazooId = '', search = '', isGroupChat = '' } = {}) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/get-user-message-list?chatType=${chatType}&kazooId=${kazooId}&search=${search}${isGroupChat !== '' ? `&isGroupChat=${isGroupChat}` : ''}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getNewChatUsers = createAsyncThunk(
	'messages/getNewChatUsers',
	async ({ chatType = 'outSide', kazooId = '', search = '', isGroupChat = '' } = {}) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/get-direct-chat-users?chatType=${chatType}&kazooId=${kazooId}&search=${search}${isGroupChat !== '' ? `&isGroupChat=${isGroupChat}` : ''}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk to send a new message to another user
export const postMessages = createAsyncThunk(
	'api/messages/postMessages',
	async (data) => {
		try {
			const isFormData = data instanceof FormData
			const res = await fetch(
				`${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/send-message/${isFormData ? data.get('otherMemberId') : data.otherMemberId}`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						...(isFormData ? {} : { 'Content-Type': 'application/json' }),
					},
					body: isFormData ? data : JSON.stringify({
						text: data.text,
						kazooId: data.kazooId || '',
					}),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const blockUser = createAsyncThunk(
	'api/messages/blockUser',
	async (otherMemberId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${ACCOUNT_API_VERSION}/accounts/block-user/${otherMemberId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			result.otherMemberId = otherMemberId
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const unblockUser = createAsyncThunk(
	'api/messages/unblockUser',
	async (otherMemberId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${ACCOUNT_API_VERSION}/accounts/unblock-user/${otherMemberId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			result.otherMemberId = otherMemberId
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const searchUnblockedUsers = createAsyncThunk(
	'api/messages/searchUnblockedUsers',
	async (payload = {}) => {
		try {
			const queryString = new URLSearchParams(
				Object.entries(payload).filter(([_, value]) => value != null)
			).toString()

			const url = `${REQUEST_URL}/${ACCOUNT_API_VERSION}/accounts/search-unblocked-users${queryString ? `?${queryString}` : ''}`

			const res = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error searching unblocked users:', err.message)
		}
	}
)

export const deleteConversation = createAsyncThunk(
	'api/messages/deleteConversation',
	async ({ otherMemberId, groupId, kazooId }) => {
		try {
			let url = `${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/clear-conversation?`

			if (groupId) {
				// Group conversation - use groupId and kazooId query parameters
				url += `groupId=${groupId}`
				if (kazooId) {
					url += `&kazooId=${kazooId}`
				}
			} else if (otherMemberId) {
				// Direct conversation - use otherUserId and kazooId query parameters
				url += `otherUserId=${otherMemberId}`
				if (kazooId) {
					url += `&kazooId=${kazooId}`
				}
			}

			const res = await fetch(url, {
				method: 'DELETE',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const result = await res.json()
			if (result.success) {
				toastHelper(
					result.message || 'Conversation deleted successfully',
					'success'
				)
			} else {
				toastHelper(result.message || 'Failed to delete conversation', 'error')
			}
			result.otherMemberId = otherMemberId
			result.groupId = groupId
			return result
		} catch (err) {
			console.log(err.message)
			toastHelper('Failed to delete conversation', 'error')
		}
	}
)

export const muteUnmuteUserNotification = createAsyncThunk(
	'api/messages/muteUnmuteUserNotification',
	async ({ userId, is_mute_chat_notification }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${ACCOUNT_API_VERSION}/accounts/mute-unmute-chat-user-notification/${userId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ is_mute_chat_notification }),
				}
			)
			const result = await res.json()
			return { ...result, userId, isMuted: is_mute_chat_notification }
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const markMessagesAsRead = createAsyncThunk(
	'api/messages/markMessagesAsRead',
	async ({ otherMemberId, kazooId }) => {
		try {
			let url = `${REQUEST_URL}/${MESSAGE_API_VERSION}/messages/mark-as-read/${otherMemberId}`
			if (kazooId) {
				url += `?kazooId=${kazooId}`
			}

			const res = await fetch(url, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const result = await res.json()
			result.otherMemberId = otherMemberId
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Initial state for the message slice
const initialState = {
	messages: [], // List of messages in the current chat
	currentSelectedUser: null, // Object of the currently selected user in chat
	directChatList: [],
	groupSelectableUsers: [],
	newChatUserList: [],
	newChatLoading: false,
	unBlockedUsers: [],
	unreadCounts: {}, // Track unread counts per user { userId: count }
}

// Redux slice to manage chat messages and selected conversation
const messageSlice = createSlice({
	name: 'message',
	initialState,
	reducers: {
		// Set the currently selected user for viewing/chatting
		setCurrentSelectedUser: (state, { payload }) => {
			state.currentSelectedUser = payload
		},

		// Append a new incoming message to the current list (e.g. from a socket)
		updateCurrentMessages: (state, { payload }) => {
			state.messages = [payload, ...state.messages]
		},

		// Reset the message state (used on logout or leaving chat)
		resetMessageSlice: () => {
			return initialState
		},

		// Update unread count for a specific user
		updateUnreadCount: (state, { payload }) => {
			const { userId, count } = payload
			if (count > 0) {
				state.unreadCounts[userId] = count
			} else {
				delete state.unreadCounts[userId]
			}

			// Update in directChatList
			const userIndex = state.directChatList.findIndex(u => u._id === userId)
			if (userIndex !== -1) {
				state.directChatList[userIndex].unreadCount = count
			}
		},

		// Update latest message in chat list when new message arrives
		updateChatListMessage: (state, { payload }) => {
			const { message, currentUserId, senderData } = payload

			// Determine the other user ID
			// The other user is whoever is NOT the current user
			let otherUserId

			if (message.senderId === currentUserId) {
				// I sent this message, so the other user is the receiver
				otherUserId = message.receiverId
			} else {
				// I received this message, so the other user is the sender
				otherUserId = message.senderId
			}

			// Find user in directChatList
			const userIndex = state.directChatList.findIndex(u => u._id === otherUserId)

			if (userIndex !== -1) {
				// User exists - update latest message and move to top
				const user = state.directChatList[userIndex]

				let formattedText = message.text ? message.text : message.fileUrl ? "📎Attachment" : ''
				const isSent = String(message.senderId) === String(currentUserId)
				if (isSent && formattedText) {
					formattedText = 'You: ' + formattedText
				} else if (!isSent && formattedText) {
					const friendName = user.businessName || user.preferredName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
					formattedText = friendName ? friendName + ': ' + formattedText : formattedText
				}

				user.latestMessage = {
					...message,
					text: formattedText
				}

				// Move to top of list
				state.directChatList.splice(userIndex, 1)
				state.directChatList.unshift(user)
			} else if (senderData && message.senderId === otherUserId) {
				// User not in list but we received senderData
				const friendName = senderData.businessName || senderData.preferredName || `${senderData.firstName || ''} ${senderData.lastName || ''}`.trim()

				let formattedText = message.text ? message.text : message.fileUrl ? 'Attachment' : ''
				if (formattedText) {
					formattedText = friendName ? friendName + ': ' + formattedText : formattedText
				}

				const newUser = {
					...senderData,
					_id: otherUserId,
					latestMessage: {
						...message,
						text: formattedText
					},
					canChat: true,
					blocked: false,
				}
				state.directChatList.unshift(newUser)
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getMessages.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to get messages',
						'error'
					)
					return
				}
				state.messages = payload.messages
			})

			.addCase(getDirectChatUsers.fulfilled, (state, { payload, meta }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to get direct chat users',
						'error'
					)
					return
				}
				if (meta.arg?.isGroupChat !== undefined && meta.arg?.isGroupChat !== '') {
					state.groupSelectableUsers = payload.directChatList
				} else {
					state.directChatList = payload.directChatList
				}
			})

			.addCase(getNewChatUsers.pending, (state) => {
				state.newChatLoading = true
			})
			.addCase(getNewChatUsers.fulfilled, (state, { payload }) => {
				state.newChatLoading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to get new chat users',
						'error'
					)
					return
				}
				state.newChatUserList = payload.directChatList
			})
			.addCase(getNewChatUsers.rejected, (state) => {
				state.newChatLoading = false
			})

			.addCase(postMessages.pending, (state, { meta }) => {
				const { text, otherMemberId, tempId, senderId } = meta.arg
				if (!tempId || !senderId) return // Skip if not optimistic

				const optimisticMsg = {
					_id: tempId,
					tempId,
					senderId,
					receiverId: otherMemberId,
					text,
					status: 'sending',
					createdAt: new Date().toISOString(),
				}

				// 1. Update message list
				state.messages = [optimisticMsg, ...state.messages]

				// 2. Update sidebar
				const userIndex = state.directChatList.findIndex(u => u._id === otherMemberId)
				if (userIndex !== -1) {
					const user = state.directChatList[userIndex]
					user.latestMessage = {
						...optimisticMsg,
						text: 'You: ' + (text || '📎Attachment')
					}
					state.directChatList.splice(userIndex, 1)
					state.directChatList.unshift(user)
				}
			})

			// Handle message post response
			.addCase(postMessages.fulfilled, (state, { payload, meta }) => {
				if (!payload || !payload.success) {
					// Mark optimistic message as failed if it exists
					const tempId = meta.arg?.tempId
					if (tempId) {
						state.messages = state.messages.map(m =>
							m.tempId === tempId ? { ...m, status: 'failed' } : m
						)
					}
					toastHelper(
						payload?.message || 'Failed to send message',
						'error'
					)
					return
				}

				const tempId = meta.arg?.tempId
				if (tempId) {
					// Replace optimistic message with actual message
					state.messages = state.messages.map(m =>
						m.tempId === tempId ? payload.sentMessage : m
					)
				} else {
					// Fallback for non-optimistic (e.g. files)
					state.messages = [payload.sentMessage, ...state.messages]
				}

				// Update directChatList - the other member is the receiver
				const otherMemberId = payload.sentMessage.receiverId

				const existingUserIndex = state.directChatList.findIndex(
					(user) => user._id === otherMemberId
				)

				if (existingUserIndex !== -1) {
					// User exists, move to top and update latest message
					const user = state.directChatList[existingUserIndex]
					user.latestMessage = {
						...payload.sentMessage,
						text: 'You: ' + (payload.sentMessage.text || (payload.sentMessage.fileUrl ? "📎Attachment" : ''))
					}
					state.directChatList.splice(existingUserIndex, 1)
					state.directChatList.unshift(user)
				} else if (state.currentSelectedUser) {
					// User does not exist, add to top
					const newUser = {
						...state.currentSelectedUser,
						latestMessage: {
							...payload.sentMessage,
							text: 'You: ' + (payload.sentMessage.text || (payload.sentMessage.fileUrl ? '📎Attachment' : ''))
						},
					}
					state.directChatList.unshift(newUser)
				}
			})

			.addCase(blockUser.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to block user',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Successfully blocked user',
					'success'
				)

				state.currentSelectedUser = {
					...state.currentSelectedUser,
					blocked: true
				}
				state.directChatList = state.directChatList.map((user) => {
					if (user._id === payload.otherMemberId) {
						return {
							...user,
							blocked: true
						}
					}
					return user
				})
			})

			.addCase(unblockUser.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to unblock user',
						'error'
					)
					return
				}
				toastHelper(
					payload?.message || 'Successfully unblocked user',
					'success'
				)

				state.currentSelectedUser = {
					...state.currentSelectedUser,
					blocked: false
				}
				state.directChatList = state.directChatList.map((user) => {
					if (user._id === payload.otherMemberId) {
						return {
							...user,
							blocked: false
						}
					}
					return user
				})
			})

			.addCase(searchUnblockedUsers.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to search unblocked users',
						'error'
					)
					return
				}
				state.unBlockedUsers = payload.users
			})

			.addCase(deleteConversation.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}

				if (payload.groupId) {
					// Group conversation deleted - handled by groupSlice
					// Just clear messages if this was the current conversation
					if (state.currentSelectedUser?._id === payload.groupId) {
						state.messages = []
					}
				} else if (payload.otherMemberId) {
					// Direct conversation deleted
					// Clear messages for the deleted conversation
					state.messages = []
					// Remove the conversation from directChatList
					state.directChatList = state.directChatList.filter(
						(user) => user._id !== payload.otherMemberId
					)
					// Clear current selected user if it's the deleted conversation
					if (state.currentSelectedUser?._id === payload.otherMemberId) {
						state.currentSelectedUser = null
					}
				}
			})

			.addCase(markMessagesAsRead.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					return
				}

				// Update unread count to 0 for this user
				const userId = payload.otherMemberId
				delete state.unreadCounts[userId]

				// Update in directChatList
				const userIndex = state.directChatList.findIndex(
					(u) => u._id === userId
				)
				if (userIndex !== -1) {
					state.directChatList[userIndex].unreadCount = 0
				}
			})

			.addCase(muteUnmuteUserNotification.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to mute/unmute notification',
						'error'
					)
					return
				}
				toastHelper(payload.message, 'success')

				if (state.currentSelectedUser?._id === payload.userId) {
					state.currentSelectedUser = {
						...state.currentSelectedUser,
						isMuted: payload.isMuted,
					}
				}

				state.directChatList = state.directChatList.map((user) => {
					if (user._id === payload.userId) {
						return { ...user, isMuted: payload.isMuted }
					}
					return user
				})
			})
	},
})

export const {
	setCurrentSelectedUser,
	updateCurrentMessages,
	resetMessageSlice,
	updateUnreadCount,
	updateChatListMessage,
} = messageSlice.actions
export default messageSlice.reducer
